import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import axiosInstance from 'config/axiosInstance';
import { MAX_PAGE_SIZE } from 'config/constant';
import { formatBusiness } from 'config/time';
import { useCompanyTimeZone } from 'reusable/CompanyTimeZone';
import { isImeComposing } from 'reusable/isImeComposing';
import { useModalDialog } from 'reusable/useModalDialog';
import { useSaveError } from 'reusable/useSaveError';
import { useUtcOffsetChange } from 'reusable/useUtcOffsetChange';

/**
 * 会社の設定（時間帯）。EV-116 段階2B（TZ-AC16）。
 *
 * 2026-10-11 PM 決定1・2・8・10:
 * - その会社の manager なら誰でも、ここで時間帯を変えられる。変更は履歴で追う。
 * - 選べるのはサーバーの一覧（RV-259 の検査を通る IANA 名のすべて）。ブラウザの一覧は使わない
 *   （ブラウザとサーバーで表の版が違っても、選べる名前をサーバーの検査と一致させるため）。
 * - 変える前に必ず確認を出す（文言は 2026-10-11 PM 承認）。既定のボタンは「やめる」。
 * - 同じ値を選んだときは保存しない。
 */

type CompanySettingsData = { name: string; time_zone: string; utc_offset: string };
type TimeZoneOption = { name: string; utc_offset: string };
type TimeZoneChange = {
  id: number;
  changed_at: string;
  changed_by: { id: number; name: string } | null;
  old_time_zone: string;
  new_time_zone: string;
};

const CHANGES_PATH = '/v1/company/time_zone_changes/';
/** 履歴を読むページ数の上限（1ページ MAX_PAGE_SIZE 件）。サーバーの next が続いても無限に読まない。 */
const MAX_CHANGE_PAGES = 50;

/**
 * 時間帯の変更の履歴を全ページ読む。RV-274（PR #216 の自動レビュー）。
 *
 * 履歴の API は全体共通のページ送り（既定10件）で、以前は1ページ目しか読まず、画面にページ送りも
 * 無かったので、11件目以降の古い変更が見えなかった。next の page を辿って読み、まとめて返す。
 * 同じページを2度読まない・ページ数に上限を置く。途中で失敗したら例外のまま返す（呼び出し元が
 * 読み込みの失敗として出す）。next の URL はサーバーが見た host で作られるので、page だけを使う。
 */
async function fetchAllChanges(): Promise<TimeZoneChange[]> {
  const rows: TimeZoneChange[] = [];
  const seen = new Set<number>();
  let page: number | null = 1;
  while (page !== null && !seen.has(page) && seen.size < MAX_CHANGE_PAGES) {
    seen.add(page);
    const response: { data?: { results?: TimeZoneChange[]; next?: string | null } } = await axiosInstance.get(
      `${CHANGES_PATH}?page=${page}&page_size=${MAX_PAGE_SIZE}`
    );
    rows.push(...(response.data?.results ?? []));
    const next = response.data?.next;
    const nextPage = next ? Number(new URL(next, 'http://localhost').searchParams.get('page')) : NaN;
    page = Number.isInteger(nextPage) && nextPage > 0 ? nextPage : null;
  }
  return rows;
}

/** 候補の表示（`Asia/Tokyo (UTC+09:00)`）。差はサーバーが返した今の値。 */
const zoneLabel = (zone: TimeZoneOption) => `${zone.name} (UTC${zone.utc_offset})`;

const CompanySettings: React.FC = () => {
  const { t } = useTranslation('layout');
  const { apply } = useCompanyTimeZone();
  const { showSaveError, saveErrorPopup } = useSaveError();

  const [company, setCompany] = useState<CompanySettingsData | null>(null);
  const [zones, setZones] = useState<TimeZoneOption[]>([]);
  const [changes, setChanges] = useState<TimeZoneChange[]>([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TimeZoneOption | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  // ADM-16-AC2 と同じ combobox の操作。矢印キーで選んでいる候補の位置（-1 は無し）。
  const [activeIndex, setActiveIndex] = useState(-1);
  const [confirming, setConfirming] = useState(false);
  // RV-281。確認のダイアログの {前}・{後} は開いた時点の値のままにする（開いている間に読み直しても変えない）。
  const [confirmLabels, setConfirmLabels] = useState<{ before: string; after: string } | null>(null);
  // 読み直しの後に、選んだ候補の差を新しくするため（入力中の選択は消さない）。
  const selectedRef = useRef<TimeZoneOption | null>(null);
  selectedRef.current = selected;
  // 古い読み込みの応答で新しい結果を上書きしない。
  const latestLoad = useRef(0);
  const [saving, setSaving] = useState(false);

  const inputId = useId();
  const listboxId = useId();
  const titleId = useId();
  const optionId = (index: number) => `${listboxId}-option-${index}`;
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const request = ++latestLoad.current;
    try {
      const [companyResponse, zonesResponse, allChanges] = await Promise.all([
        axiosInstance.get('/v1/company/'),
        axiosInstance.get('/v1/company/time_zones/'),
        fetchAllChanges(),
      ]);
      if (request !== latestLoad.current) return;
      const nextZones: TimeZoneOption[] = zonesResponse.data ?? [];
      setCompany(companyResponse.data);
      setZones(nextZones);
      setChanges(allChanges);
      // RV-281。選んである候補は消さず、差だけを新しい値にする。欄の文字が選んだ候補の表示のままなら、
      // 欄の文字も新しい差にする（利用者が打ち直している途中なら触らない）。
      const current = selectedRef.current;
      const fresh = current ? nextZones.find((zone) => zone.name === current.name) : undefined;
      if (current && fresh && fresh.utc_offset !== current.utc_offset) {
        setSelected(fresh);
        setQuery((text) => (text === zoneLabel(current) ? zoneLabel(fresh) : text));
      }
    } catch (error) {
      if (request !== latestLoad.current) return;
      showSaveError(error, t('ui.fetch-failed', { ns: 'common' }));
    }
  }, [showSaveError, t]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * RV-281（PR #216 の自動レビュー）。開いたままの画面の UTC との差を新しくする。以前は最初の読み込みの
   * 値のまま残り、夏時間に入る（抜ける）と今の時間帯・候補・確認の差が古くなっていた。
   * - 画面への復帰（focus・visibilitychange）で、会社の設定と候補を読み直す。
   * - 今の時間帯の次の差の切り替わりの時刻（＋1秒）に読み直す（ブラウザが知らない時間帯は復帰だけ）。
   * 候補は400件余りあり、切り替わりの時刻はそれぞれ違うので、候補ごとのタイマーは置かない。候補を
   * 見るのは画面を操作しているときで、戻ってきたときの読み直しで足りる。
   */
  useEffect(() => {
    const onFocus = () => {
      load();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // 今の時間帯の次の差の切り替わりの時刻に読み直す。上限より先の切り替わりは、上限で起きて
  // 次のタイマーを置き直す（RV-284。読み直しが同じ値を返しても途切れない）。
  useUtcOffsetChange(company?.time_zone, load);

  const needle = query.trim().toLowerCase();
  const filtered = needle ? zones.filter((zone) => zoneLabel(zone).toLowerCase().includes(needle)) : zones;

  const currentLabel = company ? `${company.time_zone} (UTC${company.utc_offset})` : '';
  const unchanged = !selected || !company || selected.name === company.time_zone;

  const choose = (zone: TimeZoneOption) => {
    setSelected(zone);
    setQuery(zoneLabel(zone));
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setSelected(null);
    setIsOpen(true);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (isImeComposing(event)) return; // EV-120
    const count = filtered.length;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const down = event.key === 'ArrowDown';
        if (!isOpen) {
          setIsOpen(true);
          setActiveIndex(count === 0 ? -1 : down ? 0 : count - 1);
          return;
        }
        if (count === 0) return;
        setActiveIndex((index) => {
          if (index < 0) return down ? 0 : count - 1;
          return (index + (down ? 1 : -1) + count) % count;
        });
        return;
      }
      case 'Enter': {
        if (!isOpen) return;
        event.preventDefault();
        const zone = filtered[activeIndex];
        if (zone) choose(zone);
        return;
      }
      case 'Escape': {
        if (!isOpen) return;
        event.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        return;
      }
      case 'Tab': {
        setIsOpen(false);
        setActiveIndex(-1);
        return;
      }
      default:
    }
  };

  const openConfirm = () => {
    // 同じ値なら保存しない（確認も出さない）。
    if (unchanged || !selected) return;
    setConfirmLabels({ before: currentLabel, after: zoneLabel(selected) });
    setConfirming(true);
  };

  const closeConfirm = useCallback(() => setConfirming(false), []);
  const dialogRef = useModalDialog(confirming, closeConfirm);

  const save = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const response = await axiosInstance.patch('/v1/company/', { time_zone: selected.name });
      setConfirming(false);
      setCompany(response.data);
      setSelected(null);
      setQuery('');
      // 画面全体を新しい時間帯にする（レイアウトが業務の画面を作り直し、データを取り直す）。
      apply(response.data?.time_zone);
      // RV-277。保存で足された履歴の行を、画面の作り直し（時間帯が変わったとき）に頼らず読み直す。
      // ブラウザが知らない時間帯を保存し、実効の時間帯が Asia/Tokyo のままのときは作り直されない。
      await load();
    } catch (error) {
      setConfirming(false);
      showSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="px-4 py-3 space-y-6">
      <h1 className="font-bold text-title">{t('settings.title')}</h1>

      <div className="bg-white rounded-md p-4 space-y-4 max-w-2xl">
        <div>
          <p className="text-caption text-neutral/100">{t('settings.current')}</p>
          <p className="text-subtitle-1">{currentLabel}</p>
        </div>

        <div className="relative">
          <label htmlFor={inputId} className="block mb-1 text-sm text-neutral/100">
            {t('settings.time-zone')}
          </label>
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-activedescendant={isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined}
            className="w-full p-2 border border-neutral/50 rounded-md h-12 px-4"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
          />
          {isOpen && (
            <ul
              id={listboxId}
              role="listbox"
              aria-labelledby={inputId}
              className="absolute z-10 w-full max-h-64 overflow-auto bg-white border border-neutral/50 rounded-md mt-1"
            >
              {filtered.map((zone, index) => (
                <li
                  key={zone.name}
                  id={optionId(index)}
                  role="option"
                  aria-selected={selected?.name === zone.name}
                  className={`px-4 py-2 cursor-pointer ${index === activeIndex ? 'bg-primary/50' : ''}`}
                  // 欄の blur より先に選ぶ（mousedown で欄のフォーカスを外さない）。
                  data-combobox-option
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(zone)}
                >
                  {zoneLabel(zone)}
                </li>
              ))}
            </ul>
          )}
        </div>

        <button
          type="button"
          className="px-6 h-10 rounded-md bg-primary/300 text-white disabled:opacity-60"
          disabled={unchanged || saving}
          onClick={openConfirm}
        >
          {t('settings.save')}
        </button>
      </div>

      <div className="bg-white rounded-md p-4 space-y-2">
        <h2 className="text-subtitle-1 font-medium">{t('settings.history')}</h2>
        <table className="w-full text-left">
          <thead>
            <tr>
              <th className="py-2">{t('settings.changed-at')}</th>
              <th className="py-2">{t('settings.changed-by')}</th>
              <th className="py-2">{t('settings.before')}</th>
              <th className="py-2">{t('settings.after')}</th>
            </tr>
          </thead>
          <tbody>
            {changes.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-2 text-neutral/100">
                  {t('settings.no-history')}
                </td>
              </tr>
            ) : (
              changes.map((change) => (
                <tr key={change.id} className="border-t">
                  <td className="py-2">{formatBusiness(change.changed_at, 'yyyy/MM/dd HH:mm', '-')}</td>
                  <td className="py-2">{change.changed_by?.name ?? '-'}</td>
                  <td className="py-2">{change.old_time_zone}</td>
                  <td className="py-2">{change.new_time_zone}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {confirming && company && selected && confirmLabels && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
          <div
            className="bg-white py-6 rounded-lg shadow-lg space-y-4 max-w-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            ref={dialogRef}
            tabIndex={-1}
          >
            <h2 id={titleId} className="text-title px-6">
              {t('settings.confirm-title')}
            </h2>
            <p className="px-6 border-y py-4">
              {t('settings.confirm-body', confirmLabels)}
            </p>
            <div className="flex justify-between space-x-4 px-6">
              {/* 既定のボタンは「やめる」（最初にフォーカスが入る）。 */}
              <button
                type="button"
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-500 rounded hover:bg-gray-100"
                onClick={closeConfirm}
              >
                {t('settings.confirm-cancel')}
              </button>
              <button
                type="button"
                className="flex-1 px-4 py-2 bg-primary/300 text-white rounded disabled:opacity-60"
                disabled={saving}
                onClick={save}
              >
                {t('settings.confirm-change')}
              </button>
            </div>
          </div>
        </div>
      )}
      {saveErrorPopup}
    </section>
  );
};

export default CompanySettings;
